import * as React from 'react';
import {A11y, Navigation, Pagination, Scrollbar} from 'swiper';
import {Swiper, SwiperSlide} from 'swiper/react';

import {gaLegacyCustomEvent} from '../../../client/ga/ga-legacy.functions';
import {GuidedNav} from '../guided-nav/GuidedNav';
import {ProductImage} from '../../ui/ProductImage/ProductImage';
import {SearchResult} from '../search.types';
import {URLBuilder} from '../../../server/tools/url-builder/url-builder.class';
import {PageHeader} from '../../ui/PageHeader/PageHeader';
import {swiperSlideContent, swiperSlideStyles} from '../../ui/Swiper/SwiperClasses';

interface CategorySpinnerProps {
    buildURL: URLBuilder;
    debugWebcat: boolean;
    resultList?: SearchResult;
}

export const CategorySpinner = ({buildURL, debugWebcat, resultList}: CategorySpinnerProps) => {
    const gaCategoryPath =
        resultList.breadcrumbs && resultList.breadcrumbs.length
            ? resultList.breadcrumbs[resultList.breadcrumbs.length - 1].categoryPath
            : ``;

    /**
     * Templates
     */
    if (resultList.layout === `campaign` || resultList.layout === `collection`) {
        return (
            <div>
                <PageHeader
                    title={<div style={{display: `inline`}}>{resultList.title ? resultList.title : `On Sale`}</div>}
                    type="h4"
                />
                {resultList.subCatsByCount?.length > 0 && (
                    <Swiper
                        breakpoints={{
                            0: {
                                slidesPerGroup: 1,
                                slidesPerView: 1,
                            },
                            480: {
                                slidesPerGroup: 2,
                                slidesPerView: 2,
                                spaceBetween: 20,
                            },
                            768: {
                                slidesPerGroup: 3,
                                slidesPerView: 3,
                                spaceBetween: 20,
                            },
                            1024: {
                                slidesPerGroup: 4,
                                slidesPerView: 4,
                                spaceBetween: 20,
                            },
                            1200: {
                                slidesPerGroup: 5,
                                slidesPerView: 5,
                                spaceBetween: 20,
                            },
                        }}
                        className={`tw-mb-3 ${swiperSlideStyles} ${swiperSlideContent}`}
                        modules={[Navigation, Pagination, Scrollbar, A11y]}
                        navigation
                        onSlideNextTransitionEnd={() => {
                            gaLegacyCustomEvent({
                                eventAction: `Next Click`,
                                eventCategory: `Category Navigation`,
                                eventLabel: window.location.href,
                            });
                        }}
                        onSlidePrevTransitionEnd={() => {
                            gaLegacyCustomEvent({
                                eventAction: `Previous Click`,
                                eventCategory: `Category Navigation`,
                                eventLabel: window.location.href,
                            });
                        }}
                        watchSlidesProgress={true}
                    >
                        {resultList.subCatsByCount.map((subCat, i) => (
                            <SwiperSlide
                                className="tw-h-auto tw-border tw-px-3 tw-py-2 tw-text-center"
                                key={`cat_suggestion_${i}`}
                                style={{width: `175px`}}
                            >
                                <a
                                    className="tw-no-underline"
                                    href={buildURL.withWebcat(subCat.categoryPath)}
                                >
                                    <div style={{width: `75px`, height: `91px`, margin: `auto`}}>
                                        <ProductImage
                                            altText={subCat.name}
                                            size={75}
                                            src={subCat.imageAssetLargeThumb}
                                            srcIncludesImgThumbsPath
                                        />
                                    </div>
                                    <div>
                                        {subCat.name}&nbsp;<span className="tw-text-gray-500">({subCat.numItems})</span>
                                        {debugWebcat && `| WID: ${subCat.id}`}
                                        {resultList.debug && `| Sort:${subCat.sortOrder} | ID: ${subCat._id}`}
                                    </div>
                                </a>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                )}
                {(resultList.guidedNavFacets || resultList.jumpToLinks) && (
                    <GuidedNav
                        gaCategoryPath={gaCategoryPath}
                        resultList={resultList}
                        serializedURLBuilder={buildURL.serialize()}
                    />
                )}
            </div>
        );
    }
    if (resultList.categorySuggestions && resultList.categorySuggestions.length > 0) {
        return (
            <div>
                <PageHeader
                    type="h4"
                    title="Categories"
                />
                <Swiper
                    breakpoints={{
                        0: {
                            slidesPerGroup: 1,
                            slidesPerView: 1,
                        },
                        480: {
                            slidesPerGroup: 2,
                            slidesPerView: 2,
                            spaceBetween: 20,
                        },
                        768: {
                            slidesPerGroup: 3,
                            slidesPerView: 3,
                            spaceBetween: 20,
                        },
                        1024: {
                            slidesPerGroup: 4,
                            slidesPerView: 4,
                            spaceBetween: 20,
                        },
                        1200: {
                            slidesPerGroup: 5,
                            slidesPerView: 5,
                            spaceBetween: 20,
                        },
                    }}
                    className={`tw-mb-3 ${swiperSlideStyles} ${swiperSlideContent}`}
                    modules={[Navigation, Pagination, Scrollbar, A11y]}
                    navigation
                    onSlideNextTransitionEnd={() => {
                        gaLegacyCustomEvent({
                            eventAction: `Next Click`,
                            eventCategory: `Category Navigation`,
                            eventLabel: window.location.href,
                        });
                    }}
                    onSlidePrevTransitionEnd={() => {
                        gaLegacyCustomEvent({
                            eventAction: `Previous Click`,
                            eventCategory: `Category Navigation`,
                            eventLabel: window.location.href,
                        });
                    }}
                    watchSlidesProgress={true}
                >
                    {resultList.categorySuggestions?.length > 0 &&
                        resultList.categorySuggestions.map((categorySuggestion, i) => (
                            <SwiperSlide
                                className="tw-border tw-px-2 tw-py-3 tw-text-center hover:tw-border-gray-500"
                                key={`cat_suggestion_${i}`}
                                style={{width: `175px`, height: `auto`}}
                            >
                                <a
                                    className="hover:tw-no-underline"
                                    href={categorySuggestion.url}
                                    style={{height: `150px`}}
                                >
                                    <div style={{width: `75px`, height: `90px`, margin: `auto`}}>
                                        <ProductImage
                                            altText={categorySuggestion.name}
                                            size={75}
                                            src={categorySuggestion.imageAssetLargeThumb}
                                            srcIncludesImgThumbsPath
                                        />
                                    </div>
                                    <div>
                                        {categorySuggestion.name}&nbsp;
                                        <span className="tw-text-gray-500">({categorySuggestion.numItems})</span>
                                    </div>
                                    {categorySuggestion.parentName && (
                                        <div className="tw-text-sm tw-font-normal tw-leading-[1.3] tw-text-gray-400 tw-hidden md:tw-block">
                                            in {categorySuggestion.parentName}
                                        </div>
                                    )}
                                </a>
                            </SwiperSlide>
                        ))}
                </Swiper>
                <PageHeader
                    type="h4"
                    title="Products"
                />
            </div>
        );
    }
};
